import React, { useContext } from 'react'
import { CloseX } from '@src/components/icons'
import { useSession } from 'next-auth/react'
import Link from 'next/link'
import { WorkWithParsedSrts } from '@src/api/work/[id]'
import { useMultilingualCaptions, LanguageContext } from '@src/context/LanguageContext'
import { CaptionsContext, CaptionMode } from '@src/context/CaptionsContext'
import Select from 'react-select'
import useMediaPlayerControls from '@src/lib/useMediaPlayer'
import {
  RadioGroup, Radio, Label, Dialog, DialogTrigger, Popover, Button, OverlayArrow
} from 'react-aria-components'

const MIN_CAPTION_SIZE = 1
const MAX_CAPTION_SIZE = 4
const STEP = (MAX_CAPTION_SIZE - MIN_CAPTION_SIZE) / 8

const SettingsOverlay = ({
  close, work, isOutOfSync,
}: { close: () => void; work: WorkWithParsedSrts; isOutOfSync: boolean; }) => {
  const { data: session } = useSession()
  const multilingualCaptions = useMultilingualCaptions(work)
  const {
    captionSize, setCaptionSize, captionMode, setCaptionMode,
  } = useContext(CaptionsContext)
  const { setCaptionsPreference } = useContext(LanguageContext)
  const { handleMediaPlayerControlClick } = useMediaPlayerControls({ type: 'work', id: work.id })

  const options = (work.parsedSrts || []).map((srt) => ({ value: srt.shortDescriptor, label: srt.longDescriptor }))

  // TODO: sanity check
  const canAccessAdminControls: boolean = session?.user.isSuperAdmin || session?.user.tenantData.tenantId === work.tenantId

  return (
    <div className="settings-overlay">
      <div className="mini-header">
        <div
          className="work-title quill-content-inline"
          dangerouslySetInnerHTML={{ __html: work.title }}
        />
        {/* NOTE: we need to focus on the close button when opening the modal for accessibility reasons - WMP 8/26/24 */}
        {/* eslint-disable-next-line  */}
        <button autoFocus className="settings-icon" type="button" aria-label="Close settings menu" onClick={close}><div><CloseX /></div></button>
      </div>
      <menu>
        <div className="row item">
          <button
            className="fontsize-button"
            type="button"
            onClick={() => {
              setCaptionSize((e) => (e <= MIN_CAPTION_SIZE ? e : e - 1))
            }}
            style={{ fontSize: `${MIN_CAPTION_SIZE}em` }}
          >
            Aa
          </button>
          <input type="range" min={MIN_CAPTION_SIZE} step={STEP} max={MAX_CAPTION_SIZE} onChange={(e) => setCaptionSize(Number(e.target.value))} value={captionSize} />
          <button
            className="fontsize-button"
            style={{ fontSize: `${MAX_CAPTION_SIZE}em` }}
            type="button"
            onClick={() => {
              setCaptionSize((e) => (e >= MAX_CAPTION_SIZE ? e : e + 1))
            }}
          >
            Aa
          </button>
        </div>

        <div className="item">
          Captions language:
          <Select
            options={options}
            defaultValue={{ value: multilingualCaptions?.shortDescriptor, label: multilingualCaptions?.longDescriptor }}
            onChange={({ value }) => { setCaptionsPreference(value) }}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>

        <DialogTrigger>
          <Popover
            style={{
              zIndex: 100,
            }}
            placement="top"
            offset={-20}
          >
            <Dialog>Live captions are currently unavailable for this work. Please use the full transcript instead.</Dialog>
            <OverlayArrow>
              <svg width={12} height={12} viewBox="0 0 12 12">
                <path d="M0 0 L6 6 L12 0" />
              </svg>
            </OverlayArrow>
          </Popover>
          {isOutOfSync
            ? (
              <Button style={{
                textAlign: 'left', padding: 0, border: 'none', cursor: 'default',
              }}
              >
                <div className="item">
                  <RadioGroup
                    value={captionMode}
                    onChange={(v: CaptionMode) => {
                      setCaptionMode(v)
                    }}
                    isDisabled={isOutOfSync}
                  >
                    <Label>Text mode:</Label>
                    <div className="radio-toggle">
                      <Radio value="live">Live captions</Radio>
                      <Radio value="transcript">Full transcript</Radio>
                    </div>
                  </RadioGroup>
                </div>
              </Button>
            )
            : (
              <div className="item">
                <RadioGroup
                  value={captionMode}
                  onChange={(v: CaptionMode) => {
                    setCaptionMode(v)
                  }}
                  isDisabled={isOutOfSync}
                >
                  <Label>Text mode:</Label>
                  <div className="radio-toggle">
                    <Radio value="live">Live captions</Radio>
                    <Radio value="transcript">Full transcript</Radio>
                  </div>
                </RadioGroup>
              </div>
            )}
        </DialogTrigger>

        {canAccessAdminControls
          && (
            <>
              <div className="mediaPlayer-controls-container">
                <p><b>Admin-Only Tools</b></p>
                <div className="mediaPlayer-control-group">
                  <button type="button" onClick={() => handleMediaPlayerControlClick('pause')}> Pause</button>
                  <button type="button" onClick={() => handleMediaPlayerControlClick('play')}> Play</button>
                  <button type="button" onClick={() => handleMediaPlayerControlClick('playReset')}>Restart Loop</button>
                  <button type="button" onClick={() => handleMediaPlayerControlClick('enterPowerSave')}>Disable outputs (power saving)</button>
                  <button type="button" onClick={() => handleMediaPlayerControlClick('exitPowerSave')}>Enable outputs</button>
                </div>
                <Link href={`/admin/work/${work.id}`}>[work settings]</Link>
              </div>
            </>
          )}
      </menu>
    </div>
  )
}

export default SettingsOverlay
